<template>
  <Modal>
    <step>
      <div class="address-modal">
        <div class="row">
          <div class="col-12 col-mobile-12">
            <base-form-field
              :schema="addressSchema.address1"
              v-model="currentAddressForm.address1"
            />
          </div>
        </div>
        <div class="row">
          <div class="col-lg-6 col-md-6 col-mobile-12">
            <base-form-field
              :schema="addressSchema.addressUnitTypeId"
              v-model.number="currentAddressForm.addressUnitTypeId"
              :options="unitTypes"
            />
          </div>
          <div class="col-lg-6 col-md-6 col-mobile-12">
            <base-form-field
              :schema="addressSchema.address2"
              v-model="currentAddressForm.address2"
              autocomplete="address-line2"
            />
          </div>
        </div>
        <div class="row">
          <div class="col-lg-6 col-md-6 col-mobile-12">
            <base-form-field :schema="addressSchema.city" v-model="currentAddressForm.city" />
          </div>
          <div class="col-lg-6 col-md-6 col-mobile-12">
            <base-form-field
              :schema="addressSchema.stateId"
              v-model.number="currentAddressForm.stateId"
              :options="states"
            >
              <template #default-option> State </template>
            </base-form-field>
          </div>
        </div>
        <div class="row">
          <div class="col-lg-6 col-md-6 col-mobile-12">
            <base-form-field
              :schema="addressSchema.postalCode"
              v-model="currentAddressForm.postalCode"
            />
          </div>
          <div class="col-lg-6 col-md-6 col-mobile-12">
            <base-form-field
              :schema="addressSchema.countryId"
              v-model.number="currentAddressForm.countryId"
              :options="countries"
            >
              <template #default-option> Country </template>
            </base-form-field>
          </div>
        </div>
      </div>
      <template v-slot:actions>
        <next-button text="Save Address" @click="addAddress" :disabled="addingAddress" />
      </template>
    </step>
  </Modal>
</template>

<script setup lang="ts">
import { $api } from "@/services/api";
import { computed, reactive, watch, ref } from "vue";
import Modal from "@/components/shared/Modal.vue";
import type { FieldSchema } from "@/models/form";
import BaseFormField from "@/components/shared/BaseFormField.vue";
import { useStore } from "vuex";
import NextButton from "@/components/shared/NextButton.vue";
import Step from "@/components/shared/Step.vue";
import useValidation from "@/use/validation";
import type { Applicant, Address } from "@/models/application";
import type { Stipulation } from "@/models/opal";
import type { AddressInputRequest, StipulationInputRequest } from "@/models/api";

const store = useStore();

interface Props {
  address?: Address;
  applicant: Applicant;
  stipulation: Stipulation;
}

const props = withDefaults(defineProps<Props>(), {});

const emit = defineEmits<{
  (e: "success", updatedStip: Stipulation): void;
}>();

const { validateForm, validateAddressLine2Fields } = useValidation();
let currentAddressForm = reactive({} as any);
currentAddressForm = store.state.forms.currentAddressForm;

const addingAddress = ref<boolean>(false);

async function addAddress() {
  addingAddress.value = true;
  const valid = validateForm(addressSchema, currentAddressForm);
  if (valid) {
    store.dispatch("forms/setCurrentAddressForm", currentAddressForm);

    const addressInputRequest = store.getters["forms/AddressInputRequest"];

    const request = {
      applicationStipulationId: props.stipulation?.id,
      applicationId: props?.stipulation?.applicationId,
      addressInput: addressInputRequest as AddressInputRequest,
    } as StipulationInputRequest;

    const response = await $api.applications.addStipInput(
      props?.stipulation?.applicationId,
      request,
    );

    if (response && !response?.error) {
      // updates stip status to pending
      let updatedAddressVerificationStip = props.stipulation;
      updatedAddressVerificationStip.stipulationStatusId = response.stipulationStatusId;
      updatedAddressVerificationStip.stipulationStatusName = response.stipulationStatusName;

      emit("success", updatedAddressVerificationStip);

      store.dispatch("forms/resetCurrentAddressForm");
      store.dispatch("modal/closeCurrentAddressModal");
    } else {
      addingAddress.value = false;
    }
  } else {
    addingAddress.value = false;
  }
}

const addressSchema = reactive({
  address1: {
    name: "addrLine1",
    type: "text",
    subtype: "address",
    label: "Permanent Street Address",
    placeholder: "Street",
    required: true,
    maxLength: 50,
  } as FieldSchema,
  address2: {
    name: "addrLine2",
    type: "text",
    subtype: "address",
    label: "Unit Number",
    placeholder: "Unit #",
    required: false,
    maxLength: 25,
  } as FieldSchema,
  addressUnitTypeId: {
    name: "addressUnitTypeId",
    type: "select",
    subtype: "address",
    label: "Unit Type",
    required: false,
  } as FieldSchema,
  city: {
    name: "city",
    type: "text",
    label: "City",
    subtype: "address",
    placeholder: "City",
    required: true,
    maxLength: 40,
  } as FieldSchema,
  stateId: {
    name: "stateId",
    type: "select",
    subtype: "address",
    label: "State",
    required: true,
  } as FieldSchema,
  postalCode: {
		name: "postalCode",
    type: "zipcode",
    label: "Zip",
    placeholder: "Zip",
    errorMessage: "Zip code is required.",
    required: true,
  } as FieldSchema,
  countryId: {
		name: "countryId",
    type: "select",
    label: "Country",
    required: true,
  } as FieldSchema,
});

const states = computed(() => store.getters["lookupsModule/statesOptions"]);
const countries = computed(() => store.getters["lookupsModule/countryOptions"]);
const unitTypes = computed(() => store.getters["lookupsModule/addressUnitsOptions"]);

watch(
  () => currentAddressForm.addressUnitTypeId,
  value => {
    validateAddressLine2Fields(
      addressSchema.addressUnitTypeId,
      addressSchema.address2,
      currentAddressForm.addressUnitTypeId,
      currentAddressForm.address2,
    );
  },
  { immediate: true },
);

watch(
  () => currentAddressForm.address2,
  value => {
    validateAddressLine2Fields(
      addressSchema.addressUnitTypeId,
      addressSchema.address2,
      currentAddressForm.addressUnitTypeId,
      currentAddressForm.address2,
    );
  },
  { immediate: true },
);
</script>
<style scoped>
.address-modal {
  min-height: 100px;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
}
div.row {
  width: 100%;
  margin-right: 0;
  margin-bottom: 2%;
}
.col,
.cuscd-full-wrap .row {
  margin-right: 0px;
  margin-left: 0px;
  padding: 0px;
}

@media (max-width: 992px) {
  .row {
    display: flex;
    width: 100%;
  }
  .col-10 {
    width: 100%;
  }
}
</style>
